"use client";

/**
 * «Экшены опасности» для чипов в комментарии проктора.
 *
 * Подписи берём из бэкенд-перечисления AssignmentActionType (localized
 * {raw, name} — то же, что показывает AI-отчёт, см. useActionTypeName).
 * Здесь — только allow-list тех action_type, что являются нарушениями
 * (danger), без нейтральных событий жизненного цикла (started/finished…)
 * и manual/policy-флагов.
 *
 * В каталог попадают лишь те ключи, что реально есть в перечислении, —
 * чтобы никогда не показать сырой raw вместо человекочитаемой подписи.
 * Добавить новый признак → дописать его raw в список ниже.
 */
export const DANGER_ACTION_KEYS = [
  "head_mismatch",
  "head_many",
  "head_empty",
  "head_rotated",
  "head_depth",
  "eye_away",
  "focus_down",
  "multiple_screen",
  "extension_detected",
  "cell_phone_detected",
  "earphone_detected",
  "usb_device_detected",
  "object_detected",
  "second_camera_focused",
  "second_camera_unfocused",
  "task_process_detected",
  "copy_paste",
  "noise_detect",
  "speech_detect",
  "fullscreen_closed",
  "bluetooth_detected",
  "wifi_detected",
] as const;

/**
 * Наши собственные признаки — те, которых нет в перечислении бэка и не будет.
 * Схема [TAG:key] фронтовая: бэк хранит comment как текст, поэтому такой признак
 * ничем не отличается от остальных, кроме источника подписи (ключ перевода).
 *
 * Префикс local_ — чтобы ключ никогда не столкнулся с будущим action_type бэка.
 * Записи отсюда НЕ УДАЛЯТЬ: в сохранённых комментариях остаются токены, и без
 * записи вместо подписи вылезет сырой ключ. Максимум — убрать из пикера, оставив
 * подпись (для этого поле hidden).
 */
export const LOCAL_DANGER_ACTIONS: {
  raw: string;
  labelKey: string;
  hidden?: boolean;
}[] = [
  { raw: "local_outside_help", labelKey: "verdict-action-local_outside_help" },
  { raw: "local_other", labelKey: "verdict-action-local_other" },
  // Пункты школьных перечней (schoolVerdictPresets): в общем пикере не нужны,
  // но подпись обязана резолвиться — в сохранённых комментариях лежат их токены.
  { raw: "local_cheating_attempt", labelKey: "verdict-action-local_cheating_attempt", hidden: true },
  { raw: "local_forbidden_tools", labelKey: "verdict-action-local_forbidden_tools", hidden: true },
  {
    raw: "local_passed_independently",
    labelKey: "verdict-action-local_passed_independently",
    hidden: true,
  },
];

/** Маркер чипа внутри строки comment: [TAG:action_key] */
const TAG_RE = /\[TAG:([a-z0-9_]+)\]/g;

export type CommentSegment =
  | { type: "text"; value: string }
  | { type: "tag"; key: string };

/** Строка comment → сегменты (обычный текст / чип) для рендера */
export function parseComment(comment: string): CommentSegment[] {
  const segments: CommentSegment[] = [];
  let last = 0;
  for (const m of comment.matchAll(TAG_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) segments.push({ type: "text", value: comment.slice(last, idx) });
    segments.push({ type: "tag", key: m[1] });
    last = idx + m[0].length;
  }
  if (last < comment.length) segments.push({ type: "text", value: comment.slice(last) });
  return segments;
}

/**
 * Длина видимого текста (без токенов чипов) — для счётчика и лимита.
 * Чипы не считаются: «не считал этот новый тег».
 */
export function visibleLength(comment: string): number {
  return comment.replace(TAG_RE, "").length;
}

/** Собрать строку comment из свободного текста + упорядоченных чипов */
export function buildComment(text: string, chips: readonly string[]): string {
  const tokens = chips.map((k) => `[TAG:${k}]`);
  return [text.trim(), ...tokens].filter(Boolean).join(" ");
}
